// src/components/Layout.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Outlet, useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { XPDisplay } from './XPDisplay';
import { XPModal } from './XPModal';
import { useFeedback } from '../context/FeedbackContext';
import { useSettings } from '../context/SettingsContext';
import { analyticsService } from '../utils/analyticsService';
import { debounce } from '../utils/debounce';
import { usePageHeader } from '../context/PageHeaderContext';

import { createLogger } from '../utils/logger';
import { ConsentBanner } from './privacy/ConsentBanner';
import { CacheDebugPanel } from './CacheDebugPanel';
import { NotificationBell } from './notifications/NotificationBell';
import { NotificationPanel } from './notifications/NotificationPanel';
import { useNotifications } from '../context/NotificationContext';
import { useAdminSettings } from '../context/AdminSettingsContext';
import { authCache } from '../utils/authCache';

// Add global interface extension to handle custom window properties
declare global {
  interface Window {
    originalPushState?: History['pushState'];
    originalReplaceState?: History['replaceState'];
  }
}

const logger = createLogger({ module: 'Layout' });

// Define a Settings interface for the settings we're using
interface Settings {
  sidebarCollapsed: boolean;
  darkMode?: boolean;
  files_view_mode?: string;
  defaultCareerCardsView?: 'grid' | 'kanban';
}

const Layout: React.FC = () => {
  const { darkMode } = useDarkMode();
  const { user, isAdmin: isUserAdmin } = useAuth();
  const { showFeedback } = useFeedback();
  const { settings, updateSettings } = useSettings();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isXPModalOpen, setIsXPModalOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // Local state for optimistic UI updates
  const [localSidebarCollapsed, setLocalSidebarCollapsed] = useState(settings?.sidebarCollapsed || false);
  const { header } = usePageHeader();
  
  // Notification context for page-specific indicators
  const { getUnreadCountForPage, getTotalUnreadCount } = useNotifications();
  
  // Admin settings
  const adminSettings = useAdminSettings();

  // Use useMemo to make sure we're not recreating this function on every render
  const debouncedUpdateSettings = useMemo(() => 
    debounce(async (newSettings: Partial<Settings>) => {
      try {
        await updateSettings(newSettings);
      } catch (error) {
        logger.error('Error updating sidebar state:', error);
        // Revert local state if the server update fails
        setLocalSidebarCollapsed(settings?.sidebarCollapsed || false);
      }
    }, 300),
  [updateSettings, settings?.sidebarCollapsed]);

  // Update local state when settings change from external sources
  useEffect(() => {
    setLocalSidebarCollapsed(settings?.sidebarCollapsed || false);
  }, [settings?.sidebarCollapsed]);

  // Add event listener for opening XP modal from other components
  useEffect(() => {
    const handleOpenXPModal = () => {
      setIsXPModalOpen(true);
    };

    window.addEventListener('openXPModal', handleOpenXPModal);
    
    return () => {
      window.removeEventListener('openXPModal', handleOpenXPModal);
    };
  }, []);


  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Detect mobile devices
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle toggle collapse with optimistic update
  const handleToggleCollapse = useCallback(() => {
    // Optimistically update the local state first for immediate UI feedback
    const newCollapsedState = !localSidebarCollapsed;
    setLocalSidebarCollapsed(newCollapsedState);
    
    // Then update the server state with debounce to avoid rapid-fire requests
    debouncedUpdateSettings({
      ...settings,
      sidebarCollapsed: newCollapsedState
    });
  }, [localSidebarCollapsed, settings, debouncedUpdateSettings]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      if (debouncedUpdateSettings && typeof debouncedUpdateSettings.cancel === 'function') {
        debouncedUpdateSettings.cancel();
      }
    };
  }, [debouncedUpdateSettings]);

  // Function to clear MFA protection
  const clearMfaProtection = useCallback(() => {
    try {
      logger.debug('Layout: Clearing any remaining MFA protection before navigation');
      
      // Clear all MFA-related flags in session storage
      sessionStorage.removeItem('mfa_verification_pending');
      sessionStorage.removeItem('mfa_factor_id');
      sessionStorage.removeItem('mfa_email');
      sessionStorage.removeItem('mfa_signin_in_progress');
      sessionStorage.removeItem('mfa_verification_completed');
      sessionStorage.removeItem('mfa_ultra_protection');
      sessionStorage.removeItem('mfa_flags_set_time');
      
      // Clear global flags
      if (typeof window !== 'undefined') {
        window.__MFA_SIGNIN_STARTED = false;
        window.__mfaClickListenerAdded = false;
      }
      
      // Restore original history methods if they exist
      if (window.originalPushState) {
        history.pushState = window.originalPushState;
        delete window.originalPushState;
      }
      
      if (window.originalReplaceState) {
        history.replaceState = window.originalReplaceState;
        delete window.originalReplaceState;
      }
      
      logger.debug('Layout: MFA protection cleared before navigation');
    } catch (e) {
      logger.error('Error clearing MFA protection:', e);
    }
  }, []);
  
  // Enhanced navigation handler that clears MFA protection
  const handleNavigation = useCallback((path: string, e?: React.MouseEvent) => {
    // If this is a direct click (not from the React Router context)
    if (e) {
      e.preventDefault();
    }
    
    // Only clear MFA protection for home navigation
    if (path === '/home') {
      clearMfaProtection();
    }
    
    // Navigate programmatically which avoids any click listeners
    navigate(path);
  }, [navigate, clearMfaProtection]);

  if (!user) {
    return <Navigate to="/" />;
  }

  const navigationItems = [
    { name: 'Home', path: '/home', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { name: 'Retrospectives', path: '/retrospectives', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { name: 'Resumes', path: '/resumes', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { name: 'Job Search', path: '/job-search', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'Profile', path: '/profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { name: 'Timeline', path: '/timeline', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
    { name: 'Files', path: '/files', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
  ];

  // Add admin navigation items if user is an admin
  const adminNavigationItems = isUserAdmin ? [
    { name: 'Analytics', path: '/admin/analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { name: 'Notification Test', path: '/admin/notifications', icon: 'M15 17h5l-5 5v-5zM4 2v20l5-5h11V2H4z' },
    { name: 'Notification Analytics', path: '/admin/notification-analytics', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { name: 'Users', path: '/admin/users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { name: 'Emails', path: '/admin/emails', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'Statsig Demo', path: '/statsig-demo', icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z' }
  ] : [];

  // Use local state for responsive UI
  const sidebarWidth = localSidebarCollapsed ? 'w-20' : 'w-64';
  const mainMargin = localSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64';

  return (
    <div className="flex min-h-screen w-full bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
      {/* Mobile menu button */}
      <div className={`lg:hidden fixed z-50 ${
        isMobileMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
      } transition-opacity duration-200`}
      style={{
        top: '16px',
        left: '12px'
      }}>
        <button
          className="relative p-2 rounded-md text-gray-700 dark:text-gray-100 hover:bg-gray-100/10 dark:hover:bg-white/10"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          {/* Mobile mode: show notification dot for combined bell + banner notifications */}
          {getTotalUnreadCount() > 0 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white dark:border-gray-900"></div>
          )}
        </button>
      </div>
      {/* Mobile beta tag below burger icon when sidebar is closed */}
      {!isMobileMenuOpen && (
        <div className="lg:hidden fixed z-50 bg-blue-500 dark:bg-blue-600 text-white text-[8px] px-1 rounded-sm font-bold"
          style={{
            top: '50px',
            left: '18px'
          }}>
          BETA
        </div>
      )}
      {/* Sidebar - fixed position for desktop, sliding for mobile */}
      <nav className={`
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        fixed
        ${sidebarWidth} h-screen
        shrink-0
        transition-all duration-200 ease-in-out
        bg-white dark:bg-gray-800 text-gray-800 dark:text-white
        shadow-md dark:shadow-gray-900/50
        flex flex-col
        z-40
        top-0
        left-0
        overflow-hidden
        will-change-transform
      `}>
        <div className="grow overflow-y-auto overflow-x-hidden scrollbar-hide">
          <div className={`p-4 ${sidebarWidth}`}>
            {/* Mobile-only close button for better UX */}
            <div className="lg:hidden flex justify-between items-center mb-4">
              <div className="flex items-center">
                <div className="relative">
                  <img src="/logo.svg" alt="CareerCard Logo" className="h-8 w-8 mr-2" />
                  <div className="absolute -bottom-1 -right-1 bg-blue-500 dark:bg-blue-600 text-white text-[8px] px-1 rounded-sm font-bold">BETA</div>
                </div>
                <h1 className="text-2xl font-bold truncate">Career Card</h1>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Desktop-only title with collapse button */}
            <div className="hidden lg:flex items-center justify-between mb-4">
              <div className={`flex items-center ${localSidebarCollapsed ? 'justify-center w-full' : ''}`}>
                <div className="relative">
                  <img src="/logo.svg" alt="CareerCard Logo" className={`${localSidebarCollapsed ? 'h-10 w-10' : 'h-8 w-8 mr-2'}`} />
                  <div className={`absolute -bottom-1 -right-1 bg-blue-500 dark:bg-blue-600 text-white ${localSidebarCollapsed ? 'text-[9px]' : 'text-[8px]'} px-1 rounded-sm font-bold`}>BETA</div>
                </div>
                <h1 className={`text-2xl font-bold truncate ${localSidebarCollapsed ? 'hidden' : 'block'}`}>
                  Career Card
                </h1>
              </div>
              {!localSidebarCollapsed && (
                <button
                  onClick={handleToggleCollapse}
                  className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Collapse sidebar"
                >
                  <svg
                    className="w-5 h-5 transform transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>
            {localSidebarCollapsed && (
              <div className="hidden lg:flex justify-center mb-4">
                <button
                  onClick={handleToggleCollapse}
                  className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  aria-label="Expand sidebar"
                >
                  <svg
                    className="w-5 h-5 transform rotate-180 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </div>
            )}
            {/* XP Display and Notification Bell */}
            {localSidebarCollapsed ? (
              // Collapsed mode: Notification bell above XP display, vertically stacked
              (<div className="mb-4 w-full flex flex-col items-center space-y-2">
                <NotificationBell 
                  onClick={() => setIsNotificationPanelOpen(true)} 
                  collapsedMode={true}
                />
                <XPDisplay onClick={() => setIsXPModalOpen(true)} collapsedMode={localSidebarCollapsed} />
              </div>)
            ) : (
              // Expanded mode: Notification bell and XP display on same row
              (<div className="mb-4 w-full flex items-center justify-between">
                <XPDisplay onClick={() => setIsXPModalOpen(true)} collapsedMode={localSidebarCollapsed} />
                <NotificationBell 
                  onClick={() => setIsNotificationPanelOpen(true)} 
                  collapsedMode={false}
                />
              </div>)
            )}
            <ul className="w-full space-y-1">
              {navigationItems.map((item) => (
                <li key={item.name} className="w-full">
                  <button
                    onClick={(e) => handleNavigation(item.path, e)}
                    className={`flex items-center p-3 rounded transition-colors duration-200 w-full
                      ${location.pathname === item.path 
                        ? 'bg-gray-200 dark:bg-gray-700' 
                        : ''}
                      hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600
                      group
                    `}
                  >
                    <div className={`relative ${localSidebarCollapsed ? 'block' : 'hidden'}`}>
                      <svg
                        className="w-6 h-6 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                      {/* Desktop collapsed mode: show unread dot as overlay badge on icon */}
                      {getUnreadCountForPage(item.path) > 0 && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                    <span className={`${localSidebarCollapsed ? 'hidden' : 'flex items-center justify-between w-full'}`}>
                      <span>{item.name}</span>
                      {/* Desktop expanded mode: show unread dot next to page name */}
                      {getUnreadCountForPage(item.path) > 0 && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0"></div>
                      )}
                    </span>
                    {localSidebarCollapsed && (
                      <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {item.name}
                      </div>
                    )}
                  </button>
                </li>
              ))}
              
              {/* Admin Navigation Items */}
              {adminNavigationItems.length > 0 && (
                <>
                  {!localSidebarCollapsed && (
                    <li className="pt-2 pb-1">
                      <div className="text-xs uppercase text-gray-500 dark:text-gray-400 px-3">Admin</div>
                    </li>
                  )}
                  {adminNavigationItems.map((item) => (
                    <li key={item.name} className="w-full">
                      <button
                        onClick={(e) => handleNavigation(item.path, e)}
                        className={`flex items-center p-3 rounded transition-colors duration-200 w-full
                          ${location.pathname === item.path 
                            ? 'bg-gray-200 dark:bg-gray-700' 
                            : ''}
                          hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600
                          group
                        `}
                      >
                        <svg
                          className={`w-6 h-6 shrink-0 ${localSidebarCollapsed ? 'block' : 'hidden'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                        </svg>
                        <span className={`${localSidebarCollapsed ? 'hidden' : 'block'}`}>
                          {item.name}
                        </span>
                        {localSidebarCollapsed && (
                          <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                            {item.name}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </>
              )}
            </ul>
          </div>
        </div>
        <div className={`p-4 ${sidebarWidth} shrink-0 space-y-2 ${isMobile ? 'pb-24' : ''}`}>
          <button
            onClick={showFeedback}
            className="flex items-center p-3 rounded transition-colors duration-200 w-full group
              hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600
            "
          >
            <svg 
              className={`w-6 h-6 shrink-0 ${localSidebarCollapsed ? 'block' : 'hidden'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <span className={`${localSidebarCollapsed ? 'hidden' : 'block'}`}>
              Provide Feedback
            </span>
            {localSidebarCollapsed && (
              <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Provide Feedback
              </div>
            )}
          </button>
          <button
            onClick={(e) => handleNavigation('/settings', e)}
            className={`flex items-center p-3 rounded transition-colors duration-200 w-full group
              ${location.pathname === '/settings' 
                ? 'bg-gray-200 dark:bg-gray-700' 
                : ''}
              hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600
            `}
          >
            <svg 
              className={`w-6 h-6 shrink-0 ${localSidebarCollapsed ? 'block' : 'hidden'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={`${localSidebarCollapsed ? 'hidden' : 'block'}`}>
              Settings
            </span>
            {localSidebarCollapsed && (
              <div className="fixed left-20 ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Settings
              </div>
            )}
          </button>
        </div>
      </nav>
      {/* Page Header - fixed position like mobile menu button */}
      {header && (
        <div className="fixed top-0 left-0 right-0 z-40 pointer-events-auto">
          {header}
        </div>
      )}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      {/* Main content area - with left margin for desktop */}
      <main className={`
        grow
        h-screen
        ${mainMargin}
        bg-white dark:bg-gray-900 text-black dark:text-white
        transition-all duration-200
        relative
        ${isMobileMenuOpen ? 'brightness-50 lg:brightness-100' : ''}
        flex flex-col
        overflow-hidden
        will-change-transform
        ${header ? 'pt-16' : ''}
      `}>
        <Outlet context={location.state} />
      </main>
      <XPModal
        isOpen={isXPModalOpen}
        onClose={() => setIsXPModalOpen(false)}
        xpGained={0}
        level={1}
        totalXP={0}
        nextLevelXP={100}
      />
      {/* Privacy Consent Banner */}
      <ConsentBanner />
      {/* Cache Debug Panel - available for admin users */}
      {isUserAdmin && adminSettings.settings.showCacheDebug && <CacheDebugPanel />}
      {/* Notification Panel */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
      />
    </div>
  );
};

export default Layout;